import { MESSAGES } from '@guuru/chat-common';
/* eslint-disable default-param-last */
import { BOTS } from '@guuru/expert-common';
import type { ResultOf } from '@guuru/graphql-common/gql';
import { client, errorCode, errorType } from '@guuru/graphql-web';
import { delay as sleep } from '@guuru/helpers-common';
import { localStore, memoryStore, sessionStore } from '@guuru/storage-web';
import { i18n } from '@guuru/translation-web';
import { visitorMessage } from '../messages';
import SEND_CHAT_MESSAGE from '../mutations/SendChatMessage';
import GET_CHAT from '../queries/GetChat';
import CHAT_SETTINGS from '../queries/GetPersistedChatSettings';
import {
  setFeedbackSubmitted,
  setCurrentChatSession,
} from '../state/currentChatState';

type MessageEdge = NonNullable<
  ResultOf<typeof GET_CHAT>['chat']
>['messages']['edges'][number];

type ChatId = string | number | null;
type MessageType = keyof typeof MESSAGES;

interface GetChatURLOptions {
  chatId?: string;
  token?: string;
  qs: string;
}

interface Posting {
  text: string;
  author: string;
  isUser: boolean;
  isBot: boolean;
  type: MessageType;
  hideForUser: boolean | null;
  hideForExpert: boolean | null;
  data: Record<string, any>;
  timestamp?: number;
}

const botTypingDelayMin = 100;
const botTypingDelayMax = 200;
const botTypingEllipsisMin = 500;
const botTypingEllipsisMax = 1000;

const getPendingInputKey = (chatId: ChatId) =>
  `guuru.chat.${chatId}.pendingInput`;

const randomInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1) + min);

/**
 * API: All chat related methods
 */
const API = {
  /** Chat transcript Messaging helpers */
  messages: {
    createMessage: (
      uid: string | null,
      name: string,
      coverImage: string | null,
      isBot: boolean,
      isUser: boolean,
      isExpert: boolean,
      message: string,
      type: MessageType,
      timestamp: number,
      data: Record<string, any> = {},
      hideForExpert: boolean,
      hideForUser: boolean,
    ) => ({
      author: {
        uid,
        name,
        coverImage,
        isBot,
        isUser,
        isExpert,
      },
      data,
      message,
      i18nData: data,
      i18n: i18n.t(message, data),
      id: crypto.randomUUID(),
      type: type || MESSAGES.message,
      timestamp,
      hideForExpert: hideForExpert || false,
      hideForUser: hideForUser || false,
    }),

    createBotMessage: (
      timestamp: number,
      message: string,
      type: MessageType,
      data: Record<string, any>,
      hideForExpert: boolean,
      hideForUser: boolean,
    ) =>
      API.messages.createMessage(
        BOTS.bot.externalId,
        BOTS.bot.name,
        BOTS.bot.photoUrl,
        true,
        false,
        false,
        message,
        type,
        timestamp,
        data,
        hideForExpert,
        hideForUser,
      ),

    createAnonymousTextMessage: (
      message: string,
      hideForExpert: boolean,
      hideForUser: boolean,
      data?: Record<string, any>,
    ) =>
      API.messages.createMessage(
        null,
        '',
        null,
        false,
        true,
        false,
        message,
        MESSAGES.message,
        Date.now(),
        data || {},
        hideForExpert,
        hideForUser,
      ),

    craeteBotMessage: (
      timestamp: number | null,
      botMessageCode: string,
      data: Record<string, any> = {},
    ) =>
      API.messages.createBotMessage(
        timestamp || Date.now(),
        botMessageCode,
        MESSAGES.message,
        data,
        true,
        false,
      ),

    createMessageType: (
      timestamp: number | null,
      asSimpleMessage: boolean,
      type: MessageType,
      message: string,
      data: Record<string, any>,
      { hideForExpert = true, hideForUser = false } = {},
    ) =>
      API.messages.createBotMessage(
        timestamp || Date.now(),
        message,
        asSimpleMessage ? MESSAGES.message : type,
        data || {},
        hideForExpert,
        hideForUser,
      ),
  },

  getBotDelayMessageDelay: () =>
    randomInt(botTypingDelayMin, botTypingDelayMax),

  getBotEllipsisMessageDelay: () =>
    randomInt(botTypingEllipsisMin, botTypingEllipsisMax),

  storePartnerId: (partnerId: string) => {
    memoryStore.set('partnerId', partnerId);
  },

  storeLocale: (locale?: string) => {
    memoryStore.set('locale', locale);
  },

  storeName: (name: string | null) => {
    memoryStore.set('name', name);
  },

  storeQuestion: (question: string | null) => {
    memoryStore.set('question', question);
  },

  storeUserPredictionAnswer: (userPredictionAnswer: string | null) => {
    memoryStore.set('userPredictionAnswer', userPredictionAnswer);
  },

  storeParams: (params: Record<string, any>) => {
    memoryStore.set('params', params);
  },

  storeCustomMeta: (customMeta: Record<string, any> | null) => {
    memoryStore.set('customMeta', customMeta);
  },

  storeRetrievePartnerId: () => memoryStore.get('partnerId'),

  storeRetrieveLocale: () => memoryStore.get('locale'),

  storeRetrieveParams: () => memoryStore.get('params', {}),

  storeRetrieveCategory: () => memoryStore.get('category'),

  storeRetrieveCategoryParameter: () => memoryStore.get('categoryParameter'),

  storeRetrieveLocaleParameter: () => memoryStore.get('localeParameter'),

  storeRetrieveToken: () => {
    const params = memoryStore.get('params', {});
    return params?.token;
  },

  storeRetrieveCategorySelected: () =>
    memoryStore.get('categorySelected', false),

  storeRetrieveQuestion: () => memoryStore.get('question'),

  storeRetrieveCustomMeta: () => memoryStore.get('customMeta'),

  storeAutomationBehavior: (
    partnerId: string,
    automationBehavior: Record<string, any> | null,
  ) =>
    localStore.setObject(partnerId, 'automationBehavior', automationBehavior),

  storeRetrieveAutomationBehavior: (partnerId: string) =>
    localStore.getObject(partnerId, 'automationBehavior'),

  storeRetrieveName: () => memoryStore.get('name'),

  storeRetrieveEmail: () => memoryStore.get('email'),

  storeRetrievePendingMessages: () => memoryStore.get('pendingMessages', []),

  storeRetrieveChatId: (partnerId: string) =>
    localStore.getObject(partnerId, 'chatId'),

  storeRetrieveChannel: () => memoryStore.get('channel'),

  storeChatControlToken: (
    partnerId: string,
    chatId: string,
    chatControlToken: string,
  ) =>
    localStore.pushObjectForChat(partnerId, chatId, {
      chatId,
      timestamp: Date.now(),
      chatControlToken,
    }),

  storeRetrieveChatControlToken: (partnerId: string, chatId: string) =>
    localStore.getObjectForChat(partnerId, chatId, 'chatControlToken'),

  storeChatUserPendingInput: (chatId: string, text: string) => {
    if (!chatId || !text) return;
    sessionStore.set(getPendingInputKey(chatId), text);
  },

  storeRetrieveChatUserPendingInput: (chatId: string) => {
    if (!chatId) return null;
    return sessionStore.get(getPendingInputKey(chatId));
  },

  deleteChatUserPendingInput: (chatId: string) => {
    if (!chatId) return;
    sessionStore.remove(getPendingInputKey(chatId));
  },

  pushPendingMessages: (message: Record<string, any>) => {
    memoryStore.push('pendingMessages', message);
  },

  storeEmail: (email: string | null) => {
    memoryStore.set('email', email);
  },

  storeCategory: (category: string | null) => {
    memoryStore.set('category', category);
  },

  storeCategoryParameter: (categoryParameter: string | undefined) => {
    memoryStore.set('categoryParameter', categoryParameter);
  },

  storeLocaleParameter: (localeParameter: string) => {
    memoryStore.set('localeParameter', localeParameter);
  },

  storeCategorySelected: (categorySelected: boolean | null) => {
    memoryStore.set('categorySelected', categorySelected);
  },

  storeChatPriority: (priority: string | null) => {
    memoryStore.set('priority', priority);
  },

  storeRetrieveChatPriority: () => memoryStore.get('priority'),

  storeChatId: (partnerId: string, chatId: string | null) => {
    localStore.setObject(partnerId, 'chatId', chatId);
  },

  storeChannel: (channel: string) => {
    memoryStore.set('channel', channel);
  },

  storeVisitorRole: (partnerId: string, role: string) =>
    localStore.setObject(partnerId, 'visitorRole', role),

  storeRetrieveVisitorRole: (partnerId: string) =>
    localStore.getObject(partnerId, 'visitorRole'),

  isUser: (partnerId: string) => {
    const visitorRole = API.storeRetrieveVisitorRole(partnerId);
    const params = API.storeRetrieveParams();
    return (
      (params.isUser && !(visitorRole === 'lead')) || visitorRole === 'user'
    );
  },

  isLead: (partnerId: string) => {
    const visitorRole = API.storeRetrieveVisitorRole(partnerId);
    const params = API.storeRetrieveParams();
    return (
      (params.isLead && !(visitorRole === 'user')) || visitorRole === 'lead'
    );
  },

  storeReset: (partnerId: string) => {
    API.storeChatId(partnerId, null);
    setCurrentChatSession({ chatId: null });
    setFeedbackSubmitted(false);
    API.storeQuestion(null);
    API.storeName(null);
    API.storeEmail(null);
    API.storeCustomMeta(null);
    API.storeCategory(null);
    API.storeCategorySelected(null);
    API.storeUserPredictionAnswer(null);
    API.storeChatPriority(null);
    localStore.removeObject(partnerId, 'contactConfirmed');
    localStore.removeObject(partnerId, 'automationBehavior');
    localStore.removeObject(partnerId, 'botFinished');
    localStore.removeObject(partnerId, 'preRatingHidden');
  },

  storeResetPendingMessages: () => {
    memoryStore.set('pendingMessages', []);
  },

  getChatSettings: async (
    partnerId: string,
    locale: string = API.storeRetrieveLocaleParameter() || undefined,
    category: string = API.storeRetrieveCategoryParameter() || undefined,
    token: string = API.storeRetrieveToken() || undefined,
  ) => {
    if (!partnerId) {
      return {};
    }
    const { data } = await client.query({
      query: CHAT_SETTINGS,
      variables: {
        id: partnerId,
        locale,
        category,
        token,
      },
    });

    return data?.chatSettings ?? {};
  },

  /** Performs chat initialization from scratch */
  initChat: (pendingMessages: []) => {
    memoryStore.set('pendingMessages', pendingMessages);
    return pendingMessages;
  },

  /** Adds a new category */
  addCategory: (category: string, categoryLabel: string) => {
    const message = API.messages.createAnonymousTextMessage(
      categoryLabel,
      true,
      false,
    );
    API.storeCategory(category);
    API.storeCategorySelected(true);
    memoryStore.push('pendingMessages', message);
    return message;
  },

  /** Adds a new message from a bot */
  addBotMessage: (botMessageCode: string, delay?: boolean) =>
    sleep(delay ? API.getBotEllipsisMessageDelay() : 0).then(() => {
      const message = API.messages.craeteBotMessage(null, botMessageCode);
      memoryStore.push('pendingMessages', message);
      return message;
    }),

  addBannedMessage: (
    chatId: string,
    isUser: boolean,
    posting: { text: string },
  ) => {
    if (!isUser) {
      return;
    }
    const query = client.readQuery({
      query: GET_CHAT,
      variables: {
        id: chatId,
        isVisibleByExpert: !isUser,
        isVisibleByUser: isUser,
      },
    });
    const chat = query?.chat;
    if (!chat) {
      return;
    }

    const message = visitorMessage(Date.now(), posting.text);
    client.writeQuery({
      query: GET_CHAT,
      variables: {
        id: chatId,
        isVisibleByExpert: !isUser,
        isVisibleByUser: isUser,
      },
      data: {
        chat: {
          ...chat,
          messages: {
            edges: chat.messages.edges.concat([
              {
                __typename: 'MessageEdge',
                ...message,
              } as MessageEdge,
            ]),
          },
        },
      },
    });
  },

  addPosting: async (
    id: string,
    uid: string,
    chatId: string,
    isUser: boolean,
    isBot: boolean,
    posting: Posting,
  ) => {
    try {
      await client.mutate({
        mutation: SEND_CHAT_MESSAGE,
        variables: {
          input: {
            id: id || crypto.randomUUID(),
            userId: uid,
            chatId,
            isUser,
            isBot,
            posting,
          },
        },
      });
    } catch (e: any) {
      if (errorCode(e) === errorType.FORBIDDEN) {
        API.addBannedMessage(chatId, isUser, posting);
      } else {
        throw e;
      }
    }
  },

  addMessage: (
    id: string,
    uid: string,
    chatId: string,
    isUser: boolean,
    isBot: boolean,
    message: string,
    hideForUser: boolean | null = null,
    hideForExpert: boolean | null = null,
    type: MessageType = MESSAGES.message,
    data: Record<string, any> = {},
    timestamp?: number,
  ) =>
    API.addPosting(id, uid, chatId, isUser, isBot, {
      text: message,
      author: uid,
      isUser,
      isBot,
      type,
      hideForUser,
      hideForExpert,
      data,
      timestamp,
    }),

  getMessageStyle: () =>
    API.storeRetrieveChannel() === 'form' ? 'smartForm' : undefined,

  initStorageForNewRequest: (
    partnerId: string,
    chatId: string,
    options: Record<string, any>,
  ) => {
    if (chatId) {
      API.storeChatId(partnerId, chatId);
    }

    API.storeChannel(options?.channel || 'chat');
    API.storeUserPredictionAnswer(null);

    const params = API.storeRetrieveParams();
    if (params.customMeta) {
      try {
        const customMeta = JSON.parse(params.customMeta);
        API.storeCustomMeta(customMeta);
      } catch (_) {
        // Ignore input
      }
    }
    const { payloadName, payloadEmail, payloadQuestion } = params;
    const question = payloadQuestion || options?.payloadQuestion;
    API.storeName(payloadName);
    API.storeEmail(payloadEmail);
    API.storeQuestion(question);
  },

  getChatURL: (
    partnerId: string,
    channel: string,
    { chatId, token, qs }: GetChatURLOptions = { qs: '' },
  ) => {
    const format = channel === 'form' ? 'form' : 'chats';
    let url = `/${partnerId}/${format}`;
    if (chatId) {
      url = `${url}/${chatId}/${token}`;
    }
    return `${url}${qs}`;
  },
};

/* Expose API interface */
export default API;
